<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <div class="pi-page__head">
          <h1 class="pi-page__title">Product Intelligence</h1>
          <pi-export-screen :screen="exportScreenName" :filters="exportFilters" :period="exportPeriodValue"/>
        </div>
        <pi-tabs current="features"/>
        <router-link class="pi-link pi-back" :to="backRoute">← Voltar para funcionalidades</router-link>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <pi-data-state :loading="request.loading" :error="request.error" :envelope="detailEnvelope" :empty="isEmpty"
                       :show-footer="false" @retry="load(true)">
          <section class="pi-section" aria-labelledby="pi-feature-title">
            <div class="pi-section__head">
              <h2 id="pi-feature-title" class="pi-section__title">{{ titleText }}</h2>
              <p class="pi-section__hint">{{ periodText }}</p>
              <pi-export-menu title="Indicadores" :screen="exportScreenName" :columns="indicatorColumns" :rows="indicatorRows" :envelope="request.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!request.loaded || !!request.error"/>
            </div>
            <p v-if="metaText" class="pi-muted pi-feature__meta">{{ metaText }}</p>
            <p v-if="view.configured" class="pi-notice" title="Estado atual, lido da foto diária das empresas com a funcionalidade liberada">
              {{ view.configured.text }}<template v-if="view.configured.asOfText"> (atualizado em {{ view.configured.asOfText }})</template>.
            </p>
            <div class="pi-cards">
              <pi-kpi-card label="Adoção (usuários)" :metric="adoption" definition-key="feature_adoption" info-key="features.detailAdoption"
                           :definition-version="definitionVersion" :compare="compare"/>
              <pi-kpi-card label="Profundidade" :metric="view.depth" definition-key="feature_depth" info-key="features.depth"
                           :definition-version="definitionVersion" :compare="compare"/>
              <pi-kpi-card label="Tempo até o 1º uso" :metric="view.timeToFirstUse" info-key="features.timeToFirstUse" :definition-version="definitionVersion"
                           :compare="compare"/>
            </div>
          </section>

          <section class="pi-section" aria-labelledby="pi-feature-evolution">
            <div class="pi-section__head">
              <h2 id="pi-feature-evolution" class="pi-section__title">Evolução</h2>
              <pi-definition-tooltip info-key="features.seriesAdoption" label="Adoção (7 dias)" :version="definitionVersion"/>
              <pi-export-menu title="Evolução" :screen="exportScreenName" :columns="seriesColumns" :rows="view.seriesRows" :envelope="request.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!view.seriesRows.length"/>
            </div>
            <div class="pi-chart-grid">
              <div class="pi-chart-block">
                <pi-line-chart title="Adoção no período" :series="view.adoptionSeries" unit="pct"/>
              </div>
              <div class="pi-chart-block">
                <pi-line-chart title="Usuários que usaram e elegíveis" :series="view.usersSeries"/>
              </div>
            </div>
          </section>

          <section class="pi-section" aria-labelledby="pi-feature-cuts">
            <div class="pi-section__head">
              <h2 id="pi-feature-cuts" class="pi-section__title">Cortes</h2>
              <pi-state-legend/>
            </div>
            <div class="row">
              <div class="col-xs-12 col-md-6">
                <div class="pi-feature__subhead">
                  <h3 class="pi-subtitle pi-feature__subtitle">Por papel do usuário</h3>
                  <pi-export-menu title="Por papel do usuário" :screen="exportScreenName" :columns="roleColumns" :rows="view.byRoleClass"
                                  :envelope="request.envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="!view.byRoleClass.length"/>
                </div>
                <div v-if="view.byRoleClass.length" class="pi-table-wrap">
                  <table class="pi-table">
                    <caption class="pi-sr">Adoção por perfil</caption>
                    <thead>
                    <tr>
                      <pi-th v-for="column in roleHeader" :key="column.key" :column="column"/>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in view.byRoleClass" :key="row.key">
                      <td>{{ row.label }}</td>
                      <td class="is-num">{{ row.usersText || '—' }}</td>
                      <td class="is-num"><pi-metric-cell :cell="row.adoption" :show-ratio="false"/></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="pi-muted">Sem corte por perfil.</p>
              </div>
              <div class="col-xs-12 col-md-6">
                <div class="pi-feature__subhead">
                  <h3 class="pi-subtitle pi-feature__subtitle">Por porte e perfil da empresa</h3>
                  <pi-export-menu title="Por porte e perfil da empresa" :screen="exportScreenName" :columns="segmentColumns" :rows="view.bySegment"
                                  :envelope="request.envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="!view.bySegment.length"/>
                </div>
                <div v-if="view.bySegment.length" class="pi-table-wrap">
                  <table class="pi-table">
                    <caption class="pi-sr">Adoção por porte e por perfil da empresa</caption>
                    <thead>
                    <tr>
                      <pi-th v-for="column in segmentHeader" :key="column.key" :column="column"/>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in view.bySegment" :key="row.key">
                      <td>{{ row.label }}</td>
                      <td class="is-num">{{ row.usersText || '—' }}</td>
                      <td class="is-num"><pi-metric-cell :cell="row.adoption" :show-ratio="false"/></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="pi-muted">Sem corte por porte ou perfil.</p>
              </div>
            </div>
          </section>

          <section class="pi-section" aria-labelledby="pi-feature-depth">
            <div class="pi-section__head">
              <h2 id="pi-feature-depth" class="pi-section__title">Ações usadas</h2>
              <pi-definition-tooltip info-key="features.actions" label="Ações usadas"/>
              <p class="pi-section__hint">{{ view.eventCount }} eventos no período</p>
              <pi-export-menu title="Ações usadas" :screen="exportScreenName" :columns="actionColumns" :rows="view.actions" :envelope="request.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!view.actions.length"/>
            </div>
            <pi-bar-list :rows="view.actions" empty-text="Nenhuma ação registrada para esta funcionalidade no período."/>
          </section>

          <section v-if="advancedReport" class="pi-section" aria-labelledby="pi-feature-report-usage">
            <div class="pi-section__head">
              <h2 id="pi-feature-report-usage" class="pi-section__title">Como o relatório avançado é usado</h2>
              <pi-state-legend/>
              <p class="pi-section__hint">{{ usageHint }}</p>
              <pi-export-menu title="Indicadores do relatório" :screen="exportScreenName" :columns="reportIndicatorColumns" :rows="reportIndicatorRows"
                              :envelope="usage.envelope" :filters="usageFilters" :period="exportPeriodValue"
                              :disabled="!usage.loaded || !!usage.error || !usageView.maturity.measured"/>
            </div>
            <pi-data-state :loading="usageBlocking" :error="usageFailure" :envelope="usageShownEnvelope" :empty="usageIsEmpty"
                           :empty-message="usageEmptyText" :show-footer="false" @retry="loadUsage(true)">
              <pi-advanced-report-usage :view="usageView" :sort-state="usageSort" :loading="usage.loading" :definition-version="usageDefinitionVersion"
                                        :envelope="usage.envelope" :export-screen="exportScreenName" :export-filters="usageFilters"
                                        :export-period="exportPeriodValue" :users-source="usageSource"
                                        @segment="chooseUsageSegment" @sort="sortUsage" @page="changeUsagePage" @page-size="changeUsagePageSize"/>
            </pi-data-state>
          </section>

          <section class="pi-section" aria-labelledby="pi-feature-patterns">
            <div class="pi-section__head">
              <h2 id="pi-feature-patterns" class="pi-section__title">Padrões de uso</h2>
              <p class="pi-section__hint">A janela segue a cadência da funcionalidade e fecha no fim do período; os cortes de perfil, porte e segmento não se aplicam aqui.</p>
            </div>
            <pi-data-state :loading="patterns.loading" :error="patternsFailure" :envelope="patterns.envelope" :empty="patternsAreEmpty"
                           empty-message="Nenhuma empresa elegível para agrupar nesta janela." :show-footer="false" @retry="loadPatterns(true)">
              <pi-usage-patterns :view="patternsView" :envelope="patterns.envelope" :export-screen="exportScreenName" :export-filters="patternFilters"
                                 :export-period="exportPeriodValue" :query="screenQuery"/>
            </pi-data-state>
          </section>

          <section class="pi-section" aria-labelledby="pi-feature-retention">
            <div class="pi-section__head">
              <h2 id="pi-feature-retention" class="pi-section__title">Retenção de quem fez o primeiro uso</h2>
              <p class="pi-section__hint">Semanas desde o primeiro uso; semana ainda não madura aparece como pendente, nunca como 0%.</p>
            </div>
            <template v-if="view.retention.length">
              <pi-line-chart :series="view.retentionSeries" unit="pct" :format-label="weekLabel" :height="200"/>
              <ul class="pi-feature__cells">
                <li v-for="cell in view.retention" :key="cell.week" class="pi-feature__cell" :class="{ 'is-pending': !cell.matured }">
                  <span class="pi-feature__cell-label">{{ cell.label }}</span> {{ cell.text }}
                </li>
              </ul>
            </template>
            <p v-else class="pi-muted">Sem coorte de primeiro uso no período.</p>
          </section>

          <section class="pi-section" aria-labelledby="pi-feature-company-adoption">
            <div class="pi-section__head">
              <h2 id="pi-feature-company-adoption" class="pi-section__title">Empresas por adoção</h2>
              <pi-definition-tooltip info-key="features.byCompany.adoption" label="Adoção" :version="definitionVersion"/>
              <pi-state-legend/>
              <pi-export-menu title="Empresas por adoção" :screen="exportScreenName" :columns="companyAdoptionColumns" :source="companiesSource"
                              :filters="companyAdoptionFilters" :period="exportPeriodValue"
                              :disabled="!companies.loaded || !!companies.error || !companiesView.paging.total"/>
            </div>
            <p class="pi-muted pi-feature__lead">{{ companiesView.criterionText }}</p>
            <pi-data-state :loading="companiesBlocking" :error="companiesFailure" :envelope="companiesShownEnvelope" :na-reason="companiesView.blockedText"
                           :show-footer="false" @retry="loadCompanies(true)">
              <pi-feature-companies :view="companiesView" :loading="companies.loading" @min-eligible="chooseMinEligible" @journey="chooseCompanyJourney"
                                    @sort="sortCompanies" @page="changeCompaniesPage" @page-size="changeCompaniesPageSize"/>
            </pi-data-state>
          </section>

          <section class="pi-section" aria-labelledby="pi-feature-companies">
            <div class="pi-section__head">
              <h2 id="pi-feature-companies" class="pi-section__title">Empresas com mais uso (dia de pico)</h2>
              <pi-state-legend/>
              <p class="pi-section__hint">{{ topCompaniesHint }}</p>
              <pi-export-menu title="Empresas com mais uso" :screen="exportScreenName" :columns="topCompanyColumns" :rows="view.topCompanies"
                              :envelope="request.envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="!view.topCompanies.length"/>
            </div>
            <div v-if="view.topCompanies.length" class="pi-table-wrap">
              <table class="pi-table">
                <caption class="pi-sr">Empresas com mais usuários no dia de maior uso da funcionalidade</caption>
                <thead>
                <tr>
                  <pi-th v-for="column in topCompanyHeader" :key="column.key" :column="column"/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="company in view.topCompanies" :key="company.key">
                  <td><pi-company-name :company-id="company.companyId" :shard-code="company.shardCode" :name="company.name" :query="screenQuery" stacked/></td>
                  <td class="is-num">{{ company.usersText || '—' }}</td>
                  <td class="is-num"><pi-metric-cell :cell="company.adoption" :show-ratio="false"/></td>
                </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="pi-muted">Nenhuma empresa usou a funcionalidade no período.</p>
          </section>
        </pi-data-state>

        <pi-page-footer :envelopes="[request.envelope, companies.envelope, patterns.envelope, usage.envelope]"
                        :labels="['Funcionalidade', 'Empresas por adoção', 'Padrões de uso', 'Relatório avançado']"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piKpiCard from "../../components/pi/piKpiCard.vue";
import piLineChart from "../../components/pi/piLineChart.vue";
import piBarList from "../../components/pi/piBarList.vue";
import piMetricCell from "../../components/pi/piMetricCell.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piUsagePatterns from "../../components/pi/piUsagePatterns.vue";
import piAdvancedReportUsage from "../../components/pi/piAdvancedReportUsage.vue";
import piFeatureCompanies from "../../components/pi/piFeatureCompanies.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piStateLegend from "../../components/pi/piStateLegend.vue";
import piTh from "../../components/pi/piTh.vue";
import piCompanyName from "../../components/pi/piCompanyName.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatPeriod } from "../../components/pi/piFormat.js";
import { viewPath } from "../../components/pi/piNavigation.js";
import { indicatorRows } from "../../components/pi/piOverviewModel.js";
import {
  PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, previousPeriod, resolvePeriod, toQueryString,
} from "../../components/pi/piQuery.js";
import { blankRequest, failRequest, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import {
  FEATURE_ACTION_COLUMNS, FEATURE_DETAIL_KPI_COLUMNS, FEATURE_ROLE_COLUMNS, FEATURE_SEGMENT_COLUMNS, FEATURE_SERIES_COLUMNS, FEATURE_TOP_COMPANY_COLUMNS,
  TOP_COMPANIES_HINT, detailAdoption, featureDetailEnvelope, featureDetailView, parseFeatureParam,
} from "../../components/pi/piFeaturesModel.js";
import {
  COMPANY_ADOPTION_COLUMNS, COMPANY_ADOPTION_FIELDS, DEFAULT_COMPANY_ADOPTION_LIST, companyAdoptionEnvelope, companyAdoptionError, companyAdoptionFromQuery,
  companyAdoptionLines, companyAdoptionRequest, companyAdoptionView, journeyList, minEligibleList, pagedCompanyList, resizedCompanyList, sortedCompanyList,
  withoutCompanyAdoptionQuery,
} from "../../components/pi/piFeatureCompaniesModel.js";
import { patternsEmpty, patternsError, patternsView } from "../../components/pi/piPatternsModel.js";
import {
  DEFAULT_USAGE_LIST, REPORT_KPI_COLUMNS, USAGE_FIELDS, USAGE_HINT, isAdvancedReport, pagedList, reportIndicatorItems, resizedList, sortedList, stagedList,
  usageEmpty, usageEmptyMessage, usageEnvelope, usageError, usageList, usageRequest, usageView,
} from "../../components/pi/piAdvancedReportModel.js";
import { PI_FEATURE_ADVANCED_REPORT, PI_FEATURE_COMPANIES, PI_FEATURE_DETAIL, PI_FEATURE_PATTERNS } from "../../../../urlConfig";

const FILTER_FIELDS = ["period", "compare", "roleClass", "sizeBand", "segment"];

const PATTERN_FIELDS = ["period"];

const INVALID_PARAM = {
  kind: "invalid",
  status: null,
  message: "O endereço não traz um número de funcionalidade válido.",
  retryable: false,
};

export default {
  name: "pi-feature-detail",
  components: {
    boxDefault, piTabs, piFilters, piDataState, piKpiCard, piLineChart, piBarList, piMetricCell, piPageFooter, piUsagePatterns, piAdvancedReportUsage,
    piFeatureCompanies, piDefinitionTooltip, piStateLegend, piTh, piCompanyName, piExportMenu, piExportScreen,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      filterFields: FILTER_FIELDS,
      indicatorColumns: FEATURE_DETAIL_KPI_COLUMNS,
      seriesColumns: FEATURE_SERIES_COLUMNS,
      roleColumns: FEATURE_ROLE_COLUMNS,
      roleHeader: screenColumns(FEATURE_ROLE_COLUMNS),
      segmentColumns: FEATURE_SEGMENT_COLUMNS,
      segmentHeader: screenColumns(FEATURE_SEGMENT_COLUMNS),
      actionColumns: FEATURE_ACTION_COLUMNS,
      topCompanyColumns: FEATURE_TOP_COMPANY_COLUMNS,
      topCompanyHeader: screenColumns(FEATURE_TOP_COMPANY_COLUMNS),
      companyAdoptionColumns: COMPANY_ADOPTION_COLUMNS,
      reportIndicatorColumns: REPORT_KPI_COLUMNS,
      request: blankRequest(),
      patterns: blankRequest(),
      usage: blankRequest(),
      usageList: Object.assign({}, DEFAULT_USAGE_LIST),
      usageLoadedBase: null,
      usageHint: USAGE_HINT,
      companies: blankRequest(),
      companiesList: companyAdoptionFromQuery(this.$route.query) || Object.assign({}, DEFAULT_COMPANY_ADOPTION_LIST),
      companiesLoadedBase: null,
      companiesLoadedJourney: "",
      topCompaniesHint: TOP_COMPANIES_HINT,
    };
  },
  computed: {
    featureType() {
      return parseFeatureParam(this.$route.params.featureType);
    },
    compare() {
      return compareEnabled(this.filters);
    },
    screenQuery() {
      return withoutCompanyAdoptionQuery(this.$route.query);
    },
    period() {
      return resolvePeriod(this.filters, new Date());
    },
    periodText() {
      return formatPeriod(this.period.from, this.period.to);
    },
    exportScreenName() {
      return "Funcionalidade " + this.titleText;
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    exportFilters() {
      return filterLines({ filters: this.filters, fields: FILTER_FIELDS });
    },
    patternFilters() {
      return filterLines({ filters: this.filters, fields: PATTERN_FIELDS });
    },
    usageFilters() {
      return filterLines({ filters: this.filters, fields: USAGE_FIELDS });
    },
    companyAdoptionFilters() {
      return filterLines({ filters: this.filters, fields: COMPANY_ADOPTION_FIELDS, extra: companyAdoptionLines(this.companiesView) });
    },
    indicatorRows() {
      return indicatorRows([
        { key: "adoption", metric: this.adoption },
        { key: "depth", metric: this.view.depth },
        { key: "timeToFirstUse", metric: this.view.timeToFirstUse },
      ], { period: this.period, previous: previousPeriod(this.period), compare: this.compare });
    },
    reportIndicatorRows() {
      return indicatorRows(reportIndicatorItems(this.usageView), { period: this.usageView.window });
    },
    usageSource() {
      return { path: PI_FEATURE_ADVANCED_REPORT, query: this.usageRequest.params, pick: (data) => (data ? data.users : null) };
    },
    companiesSource() {
      return { path: PI_FEATURE_COMPANIES(this.featureType || ""), query: this.companiesRequest.params };
    },
    params() {
      return buildApiQuery(this.filters, { fields: FILTER_FIELDS });
    },
    apiKey() {
      return (this.featureType || "invalid") + "?" + toQueryString(this.params);
    },
    patternsParams() {
      return buildApiQuery(this.filters, { fields: PATTERN_FIELDS });
    },
    patternsKey() {
      return (this.featureType || "invalid") + "?" + toQueryString(this.patternsParams);
    },
    patternsView() {
      return patternsView(payloadOf(this.patterns.envelope));
    },
    patternsAreEmpty() {
      return this.patterns.loaded && patternsEmpty(this.patternsView);
    },
    patternsFailure() {
      return patternsError(this.patterns.error);
    },
    advancedReport() {
      return isAdvancedReport(this.featureType);
    },
    usageRequest() {
      return usageRequest(this.filters, this.usageList);
    },
    usageKey() {
      return this.advancedReport ? this.usageRequest.key : "";
    },
    usageView() {
      return usageView(payloadOf(this.usage.envelope), {
        list: Object.assign({}, this.usageList, { page: this.usageRequest.page }),
        requestedTo: this.usageRequest.params.to,
        query: this.screenQuery,
      });
    },
    usageIsEmpty() {
      return this.usage.loaded && !!this.usage.envelope && usageEmpty(this.usageView);
    },
    usageEmptyText() {
      return usageEmptyMessage(payloadOf(this.usage.envelope));
    },
    usageShownEnvelope() {
      return this.usageIsEmpty ? this.usage.envelope : usageEnvelope(this.usage.envelope);
    },
    usageBlocking() {
      return this.usage.loading && this.usageLoadedBase !== this.usageRequest.baseKey;
    },
    usageFailure() {
      return usageError(this.usage.error);
    },
    usageSort() {
      const list = usageList(this.usageList);
      return { sort: list.sort, desc: list.desc };
    },
    usageDefinitionVersion() {
      return this.usage.envelope ? this.usage.envelope.definitionVersion : null;
    },
    definitionVersion() {
      return this.request.envelope ? this.request.envelope.definitionVersion : null;
    },
    detailEnvelope() {
      return featureDetailEnvelope(this.request.envelope);
    },
    companiesRequest() {
      return companyAdoptionRequest(this.featureType, this.filters, this.companiesList);
    },
    companiesKey() {
      return this.companiesRequest.key;
    },
    companiesView() {
      return companyAdoptionView(payloadOf(this.companies.envelope), {
        list: Object.assign({}, this.companiesList, { page: this.companiesRequest.page }),
        query: this.screenQuery,
        params: this.companiesRequest.params,
        loadedJourney: this.companiesLoadedJourney,
      });
    },
    companiesShownEnvelope() {
      return companyAdoptionEnvelope(this.companies.envelope);
    },
    companiesBlocking() {
      return this.companies.loading && this.companiesLoadedBase !== this.companiesRequest.baseKey;
    },
    companiesFailure() {
      return companyAdoptionError(this.companies.error);
    },
    view() {
      return featureDetailView(payloadOf(this.request.envelope), this.compare);
    },
    adoption() {
      return detailAdoption(this.view.adoption, this.params);
    },
    titleText() {
      return this.view.feature.name || ("Funcionalidade " + (this.featureType || ""));
    },
    metaText() {
      const feature = this.view.feature;
      const parts = [];
      if (feature.moduleText && feature.moduleText !== "—") parts.push("Módulo: " + feature.moduleText);
      if (this.featureType) parts.push("nº " + this.featureType);
      if (feature.technicalName && feature.technicalName !== feature.name) parts.push(feature.technicalName);
      if (feature.rolesText) parts.push("Perfis: " + feature.rolesText);
      if (feature.gatedText) parts.push(feature.gatedText);
      if (feature.expectedFrequency) parts.push("Frequência esperada: " + feature.expectedFrequency);
      return parts.join(" · ");
    },
    isEmpty() {
      return this.request.loaded && !Object.keys(payloadOf(this.request.envelope)).length;
    },
    backRoute() {
      return { path: viewPath("features"), query: this.screenQuery };
    },
  },
  watch: {
    apiKey() {
      this.load(false);
    },
    patternsKey() {
      this.loadPatterns(false);
    },
    usageKey() {
      this.loadUsage(false);
    },
    companiesKey() {
      this.loadCompanies(false);
    },
    "$route.query"(query) {
      const seeded = companyAdoptionFromQuery(query);
      if (seeded) this.companiesList = seeded;
    },
  },
  created() {
    this.load(false);
    this.loadPatterns(false);
    this.loadUsage(false);
    this.loadCompanies(false);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.request, key, force)) return;
      if (!this.featureType) {
        failRequest(this.request, INVALID_PARAM, key);
        return;
      }
      const featureType = this.featureType;
      runRequest(this.request, () => piGet(PI_FEATURE_DETAIL(featureType), this.params), "carregar a funcionalidade", key);
    },
    loadPatterns(force) {
      const key = this.patternsKey;
      if (!shouldLoad(this.patterns, key, force)) return;
      if (!this.featureType) {
        failRequest(this.patterns, INVALID_PARAM, key);
        return;
      }
      const featureType = this.featureType;
      runRequest(this.patterns, () => piGet(PI_FEATURE_PATTERNS(featureType), this.patternsParams), "carregar os padrões de uso", key);
    },
    loadUsage(force) {
      if (!this.advancedReport) return;
      const key = this.usageKey;
      if (!shouldLoad(this.usage, key, force)) return;
      const request = this.usageRequest;
      runRequest(this.usage, () => piGet(PI_FEATURE_ADVANCED_REPORT, request.params), "carregar o uso do relatório avançado", key)
        .then((applied) => {
          if (applied) this.usageLoadedBase = request.baseKey;
        });
    },
    chooseUsageSegment(stage) {
      this.usageList = stagedList(this.usageList, stage);
    },
    sortUsage(column) {
      this.usageList = sortedList(this.usageList, column);
    },
    changeUsagePage(page) {
      this.usageList = pagedList(this.usageList, page, this.usageRequest.baseKey);
    },
    changeUsagePageSize(size) {
      this.usageList = resizedList(this.usageList, size);
    },
    loadCompanies(force) {
      const key = this.companiesKey;
      if (!shouldLoad(this.companies, key, force)) return;
      if (!this.featureType) {
        failRequest(this.companies, INVALID_PARAM, key);
        return;
      }
      const featureType = this.featureType;
      const request = this.companiesRequest;
      runRequest(this.companies, () => piGet(PI_FEATURE_COMPANIES(featureType), request.params), "carregar as empresas por adoção", key)
        .then((applied) => {
          if (!applied) return;
          this.companiesLoadedBase = request.baseKey;
          this.companiesLoadedJourney = request.params.journey || "";
        });
    },
    chooseMinEligible(value) {
      this.companiesList = minEligibleList(this.companiesList, value);
    },
    chooseCompanyJourney(value) {
      this.companiesList = journeyList(this.companiesList, value);
    },
    sortCompanies(column) {
      this.companiesList = sortedCompanyList(this.companiesList, column);
    },
    changeCompaniesPage(page) {
      this.companiesList = pagedCompanyList(this.companiesList, page, this.companiesRequest.baseKey);
    },
    changeCompaniesPageSize(size) {
      this.companiesList = resizedCompanyList(this.companiesList, size);
    },
    weekLabel(key) {
      return "W" + Number(key);
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-page__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 12px;
}

.pi-page__head .pi-page__title {
  margin: 0;
}

.pi-feature__subhead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 8px;
}

.pi-feature__subtitle {
  margin: 0;
}

.pi-feature__meta {
  margin-bottom: 12px;
}

.pi-feature__lead {
  margin-bottom: 10px;
}

.pi-feature__cells {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pi-feature__cell {
  padding: 4px 8px;
  border: 1px solid #e6e7eb;
  border-radius: 4px;
  background: #ffffff;
  font-size: 12px;
  color: #1f2933;
}

.pi-feature__cell.is-pending {
  border-style: dashed;
  color: #6b7280;
}

.pi-feature__cell-label {
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piFeatureDetail.vue